import { isNumberString } from "@corca/core";
import { sortBy } from "es-toolkit";

import { insertedNodeProps, insertedNodeType } from "@/components/suggestions/utils/libraryItem";

import { librarySuggestionBySymbolKey } from "./librarySuggestionsProvider";
import {
  createKnownSuggestionMap,
  createPowerOperatorItem,
  createSingleLetterSuggestionMap,
  getFlattenVariants,
  getSplitStringSuggestions,
  increasePriority,
  mediumPriority,
  type TextVariantProps,
  type SuggestionVariant,
  type KnownSuggestions,
  filterVariants,
} from "./multiSuggestionsProvider/utils";
import { servedSuggestions } from "./utils/servedSuggestions";

import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { MigratedSource } from "@/models/LibrariesModel";
import type { NodeModel } from "@/models/nodes/types";

const FINAL_VARIANTS_LIMIT = 5;

type KnownMaps = {
  knownSuggestionMap: Map<string, SingleSuggestion[]>;
  knownSymbolKeywords: string[];
  singleLetterSuggestionMap: Map<string, SingleSuggestion[]>;
  singleLetterSymbols: string[];
};

function buildKnownMaps(known: KnownSuggestions): KnownMaps {
  const knownSuggestionMap = createKnownSuggestionMap(known);
  const singleLetterSuggestionMap = createSingleLetterSuggestionMap(
    new Map(
      ["e", "i", "x", "y", "z"].map((letter) => [letter, knownSuggestionMap.get(letter) ?? []]),
    ),
  );

  return {
    knownSuggestionMap,
    knownSymbolKeywords: Array.from(knownSuggestionMap.keys()),
    singleLetterSuggestionMap,
    singleLetterSymbols: Array.from(singleLetterSuggestionMap.keys()),
  };
}

const multiContext = (s: SingleSuggestion) => s.context?.includes("multi") ?? true;

const KNOWN_SOURCES: MigratedSource[] = [
  "constants.json",
  "math-variables.json",
  "physics-variables.json",
  "symbols.json",
  "functions.json",
];

const mapsByRows = new WeakMap<SingleSuggestion[], KnownMaps>();

/**
 * The known suggestions for a document, by what they insert: the library's served items plus
 * the file's for a file not served yet. Built once per served rows, like the library caches.
 */
function knownMapsFor(doc: DocumentModel): KnownMaps {
  const rows = servedSuggestions(doc, KNOWN_SOURCES);
  let maps = mapsByRows.get(rows);

  if (!maps) {
    const ofType = (type: string) =>
      rows.filter((s) => insertedNodeType(s.item) === type && multiContext(s));
    maps = buildKnownMaps({
      constants: ofType("constant"),
      variables: ofType("variable"),
      functions: ofType("function"),
    });
    mapsByRows.set(rows, maps);
  }

  return maps;
}

/**
 * The minus a negative argument needs, from every loaded library rather than the enabled ones:
 * disabling the library that holds it must not take `sin -x` away from an enabled function.
 */
function minusSuggestionFor(doc: DocumentModel): SingleSuggestion | undefined {
  return doc.libraries.serves("operator-symbols.json")
    ? librarySuggestionBySymbolKey(doc, "symbol-subtraction")
    : servedSuggestions(doc, ["operator-symbols.json"]).find(
        (s) => s.item.key === "symbol-subtraction",
      );
}

export const multiSuggestionsProvider: SuggestionProvider = {
  type: "multi",
  search: (str, doc) => {
    if (str.length <= 1) return [];

    if (!/\w+/.test(str)) return [];

    if (str.includes("-")) {
      return [collectNegativeArgumentVariants(str, doc)];
    }

    const tokens = tokenizeText(str);

    if (tokens.length > 10) {
      return [];
    }

    const maps = knownMapsFor(doc);
    const props = createTextVariantProps(doc, maps);

    let variants = collectVariants(tokens, props);

    let flattenVariants = filterVariants(getFlattenVariants(variants));

    const greedy = flattenVariants.length === 0 && str.length < 3;

    // if no variants found and input is short, perform greedy symbol matching
    if (greedy) {
      props.knownSymbolKeywords = maps.singleLetterSymbols;
      props.knownSuggestionMap = maps.singleLetterSuggestionMap;

      variants = collectVariants(tokens, props);
      flattenVariants = filterVariants(getFlattenVariants(variants));
    }

    for (const variant of flattenVariants) {
      variant.priority = increasePriority(variant.priority, variant.maxRepetition ?? 1, 1.5);
      variant.priority -= 0.5;

      if (greedy) {
        variant.priority -= 1;
        variant.popupGroup = "multi";
      }
    }

    flattenVariants = sortBy(flattenVariants, [(v) => -v.priority]);

    return [selectFunctionBucketVariants(flattenVariants, FINAL_VARIANTS_LIMIT)];
  },
};

/**
 * group variants by their function sequence so all structural readings of the
 * best-matching function (sin(2x²), sin²(x²), ...) surface before sibling
 * functions (arcsin, sinh) fill the remaining slots
 */
function selectFunctionBucketVariants(variants: SuggestionVariant[], limit: number) {
  const buckets = new Map<string, SuggestionVariant[]>();

  for (const variant of variants) {
    const key = variant.items
      .filter((item) => insertedNodeType(item) === "function")
      .map((item) => item.symbol)
      .join("×");
    let bucket = buckets.get(key);
    if (!bucket) buckets.set(key, (bucket = []));
    bucket.push(variant);
  }

  return Array.from(buckets.values()).flat().slice(0, limit);
}

type SearchDoc = Parameters<SuggestionProvider["search"]>[1];

function tokenizeText(text: string) {
  const tokens = text.split(/(\d+)/).filter(Boolean);
  if (isNumberString(tokens[0])) {
    tokens.unshift("");
  }
  return tokens;
}

function createTextVariantProps(doc: SearchDoc, maps: KnownMaps): TextVariantProps {
  const props: TextVariantProps = {
    knownSymbolKeywords: maps.knownSymbolKeywords,
    knownSuggestionMap: maps.knownSuggestionMap,
    nodeLookup: doc.nodeLookup,
    existingNodes: new Map(),
  };

  const nodeKeyMap = doc.nodeLookup?.groupByKey({ uniqBy: "labels" });

  if (nodeKeyMap) {
    for (const { base, type, ids } of nodeKeyMap.values()) {
      if (type === "function") continue;
      props.existingNodes.set(base, []);
      const firstId = ids.values().next().value;
      const node = firstId ? (doc.nodes.get(firstId) as NodeModel<"variable" | "constant">) : null;
      props.existingNodes.get(base)?.push(node);
    }
  }

  return props;
}

/**
 * only support a single minus right after a leading function name (sin-x, sin-1, sin-2x);
 * chains like cos-xsin-y are ambiguous, minus between plain symbols is subtraction,
 * and function arguments (sin-cosx) break the sibling-function heuristic in
 * buildSingleNodeFromMultiSuggest, which closes the outer argument on the next function
 */
function collectNegativeArgumentVariants(str: string, doc: SearchDoc): SuggestionVariant[] {
  const match = /^([a-z]+)-([^-]+)$/.exec(str);
  const minusSuggestion = minusSuggestionFor(doc);
  if (!match || !minusSuggestion) return [];

  const [, head, rest] = match;

  const maps = knownMapsFor(doc);
  const functionSuggestions = (maps.knownSuggestionMap.get(head) ?? []).filter(
    (s) => insertedNodeType(s.item) === "function",
  );
  if (functionSuggestions.length === 0) return [];

  const props = createTextVariantProps(doc, maps);
  const restTokens = tokenizeText(rest);

  const restVariants = collectNonFunctionVariants(restTokens, props);

  const variants: SuggestionVariant[] = [];

  for (const functionSuggestion of functionSuggestions) {
    for (const restVariant of restVariants) {
      variants.push({
        variantTypes: ["plain", "plain", ...restVariant.variantTypes],
        type: "multi",
        items: [functionSuggestion.item, minusSuggestion.item, ...restVariant.items],
        priority: mediumPriority([functionSuggestion.priority, restVariant.priority]),
        popupGroup: "multi",
      });
    }
  }

  variants.push(...collectNegativePowerVariants(functionSuggestions, restTokens, props));

  return selectFunctionBucketVariants(sortBy(variants, [(v) => -v.priority]), FINAL_VARIANTS_LIMIT);
}

function collectNonFunctionVariants(tokens: string[], props: TextVariantProps) {
  return getFlattenVariants(collectVariants(tokens, props)).filter(
    (variant) => !variant.items.some((item) => insertedNodeType(item) === "function"),
  );
}

/**
 * mirror the positive function-power reading for a negative exponent:
 * sin-1 → sin^-1(), sin-2x2 → sin^-2(x²); exponent -1 is allowed since,
 * unlike ^1, it isn't a no-op
 */
function collectNegativePowerVariants(
  functionSuggestions: SingleSuggestion[],
  restTokens: string[],
  props: TextVariantProps,
): SuggestionVariant[] {
  if (restTokens[0] !== "") return [];

  const num = restTokens[1];
  const argTokens = restTokens.slice(2);

  const argVariants: SuggestionVariant[] = argTokens.length
    ? collectNonFunctionVariants(argTokens, props)
    : [{ variantTypes: [], type: "multi", items: [], priority: 1 }];

  const variants: SuggestionVariant[] = [];

  for (const functionSuggestion of functionSuggestions) {
    if ((insertedNodeProps(functionSuggestion.item)?.powerPlacement ?? "child") !== "child")
      continue;

    for (const argVariant of argVariants) {
      variants.push({
        variantTypes: ["power", ...argVariant.variantTypes],
        type: "multi",
        items: [functionSuggestion.item, createPowerOperatorItem(`-${num}`), ...argVariant.items],
        priority: mediumPriority([functionSuggestion.priority, argVariant.priority]),
        popupGroup: "multi",
      });
    }
  }

  return variants;
}

function collectVariants(tokens: string[], props: TextVariantProps): SuggestionVariant[][] {
  const variants: SuggestionVariant[][] = [];

  for (let i = 0; i < tokens.length; i += 2) {
    const text = tokens[i];
    const num = tokens[i + 1];
    const isLast = i >= tokens.length - 2;
    variants.push(getSplitStringSuggestions(i, text, num, isLast, props));
  }

  return variants;
}
